import { Dispatch } from 'react';

import {
  ActionCenterTypes,
  FormState,
  MatchedTargets,
  OptInOptions,
  TakeActionErrorsFields,
  TakeActionFormState,
} from '@/core/interfaces/common';
import { AdvocateSignature } from '@/core/components/AdvocateSignature';
import { FormInput } from '@/core/components/FormInput';
import { FormTextarea } from '@/core/components/FormTextarea';
import { Greeting } from '@/core/components/Greeting';

import { hasAnyError } from '@/utils/helpers';

import {
  BaseSendButton,
  CenteredErrorMessage,
  ErrorMessage,
  FormHeading,
  FormInputWrapper,
  GeneralErrorMessage,
  SendButton,
  StepButton,
  SubjectInputWrapper,
  SubmitButtonWrapper,
  SubmitSection,
  TalkingPointItem,
  TalkingPointsList,
} from '@/themes/globalStyles';

import { ContactFieldsContainer } from '../ContactFieldsContainer';
import { OptCheckboxes } from '../OptCheckboxes';
import {
  ActionsWrapper,
  EmailText,
  StyledPickActionType,
} from './TakeActionFormContainer.styles';

interface TakeActionFormContainerProps {
  actionCenterType: ActionCenterTypes;
  backgroundColor: string;
  buttonColor: string;
  currentMessageIndex: number;
  emailChannel: boolean;
  error: TakeActionErrorsFields;
  formState: TakeActionFormState;
  handleBlurAddress?: (streetAddress?: string, zipCode?: string) => void;
  hideMessageHeadline?: boolean;
  isPending: boolean;
  isDisabledInputText?: boolean;
  mode: string;
  optIn: OptInOptions;
  phoneChannel: boolean;
  previewTargets: MatchedTargets;
  setActionCenterType?: Dispatch<React.SetStateAction<ActionCenterTypes>>;
  setCurrentMessageIndex: Dispatch<React.SetStateAction<number>>;
  setError: Dispatch<React.SetStateAction<TakeActionErrorsFields>>;
  setFormState: Dispatch<React.SetStateAction<TakeActionFormState>>;
  showPickActionButton?: boolean;
  talkingPoints: string;
  textColor: string;
}

export const TakeActionFormContainer = ({
  actionCenterType,
  backgroundColor,
  buttonColor,
  currentMessageIndex,
  emailChannel,
  error,
  formState,
  handleBlurAddress,
  hideMessageHeadline,
  isDisabledInputText,
  isPending,
  mode,
  optIn,
  phoneChannel,
  previewTargets,
  setActionCenterType,
  setCurrentMessageIndex,
  setError,
  setFormState,
  showPickActionButton,
  talkingPoints,
  textColor,
}: TakeActionFormContainerProps) => {
  const {
    subjectMessagePairs,
    isOptInEmail,
    isOptInText,
  } = formState;
  const {
    emailType,
    phoneType,
  } = ActionCenterTypes;

  const handleOptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked,
    } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      [name]: checked,
    }));
  };

  const handleClearSubjectField = () => {
    setError(prevValues => {
      const newErrors = { ...prevValues };

      if (newErrors.messages?.[currentMessageIndex]) {
        newErrors.messages[currentMessageIndex].subject = [];
      }

      return newErrors;
    });
  };

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFormState(prevValues => {
      const newFormState = { ...prevValues };

      newFormState.subjectMessagePairs[currentMessageIndex].subject.name = value;

      return newFormState;
    });
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
    const { value } = event.target;

    setFormState(prevValues => {
      const newFormState = { ...prevValues };

      newFormState.subjectMessagePairs[currentMessageIndex].body[index].name = value;

      return newFormState;
    });
  };

  const handleClearMessageField = () => {
    setError(prevValues => {
      const newErrors = { ...prevValues };

      if (newErrors.messages?.[currentMessageIndex]) {
        newErrors.messages[currentMessageIndex].message = [];
      }

      return newErrors;
    });
  };

  const handlePersonalMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;

    setFormState(prevValues => {
      const newFormState = { ...prevValues };

      newFormState.subjectMessagePairs[currentMessageIndex].personalMessage = value;

      return newFormState;
    });
  };

  const renderError = (fieldError: string) => (<ErrorMessage>{fieldError}</ErrorMessage>);

  const actButtonText = (): string => {
    if (actionCenterType === phoneType) {
      return 'Call';
    }

    if (mode === 'petition') {
      return 'Sign petition';
    }

    return 'Send message';
  };

  return (
    <>
      <ContactFieldsContainer
        backgroundColor={backgroundColor}
        error={error}
        formState={formState}
        handleBlurAddress={handleBlurAddress}
        setError={setError}
        setFormState={setFormState as Dispatch<React.SetStateAction<FormState>>}
        textColor={textColor}
      />
      <ActionsWrapper>
        {emailChannel && phoneChannel && setActionCenterType && showPickActionButton && (
          <StyledPickActionType
            actionCenterType={actionCenterType}
            backgroundColor={buttonColor}
            setActionCenterType={setActionCenterType}
            textColor={textColor}
          />
        )}
      </ActionsWrapper>
      {emailChannel &&
        actionCenterType === emailType &&
        (subjectMessagePairs[currentMessageIndex] ?
          (
            <>
              {!hideMessageHeadline && (
                <FormHeading>Message</FormHeading>
              )}
              <SubjectInputWrapper>
                {(
                  isDisabledInputText &&
                  !subjectMessagePairs[currentMessageIndex]?.subject?.isAllowEdit) ?
                  (
                    <EmailText>{subjectMessagePairs[currentMessageIndex]?.subject?.name || ''}</EmailText>
                  ) :
                  (
                    <FormInput
                      backgroundcolor={backgroundColor}
                      error={() => error?.messages?.[currentMessageIndex]?.subject?.[0] || false}
                      isDisabled={
                        !subjectMessagePairs[currentMessageIndex]?.subject?.isAllowEdit
                      }
                      label="Subject"
                      name="subject"
                      onChange={handleSubjectChange}
                      onFocus={handleClearSubjectField}
                      value={subjectMessagePairs[currentMessageIndex]?.subject?.name || ''}
                    />
                  )}
              </SubjectInputWrapper>
              <Greeting
                isEmailAction
                targets={previewTargets}
                mode={mode}
              />
              {subjectMessagePairs[currentMessageIndex]?.body?.length ?
                subjectMessagePairs[currentMessageIndex].body.map((messageBlock, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <FormInputWrapper key={index}>
                    {isDisabledInputText && !messageBlock.isAllowEdit ?
                      (
                        <EmailText>{messageBlock.name}</EmailText>
                      ) :
                      (
                        <FormTextarea
                          hiddenLabel={`message paragraph ${index + 1}`}
                          backgroundcolor={backgroundColor}
                          isDisabled={!messageBlock.isAllowEdit}
                          onChange={event => handleMessageChange(event, index)}
                          onFocus={handleClearMessageField}
                          value={messageBlock.name}
                        />
                      )}
                  </FormInputWrapper>
                )) :
                null
              }
              {subjectMessagePairs[currentMessageIndex]?.isAllowPersonalMessage && (
                <FormInputWrapper>
                  <FormTextarea
                    hiddenLabel="personal message"
                    backgroundcolor={backgroundColor}
                    label="Personal message"
                    onChange={event => handlePersonalMessageChange(event)}
                    onFocus={handleClearMessageField}
                    value={subjectMessagePairs[currentMessageIndex].personalMessage}
                  />
                </FormInputWrapper>
              )}
              <AdvocateSignature
                firstName={formState.firstName}
                lastName={formState.lastName}
                mode={mode}
                streetAddress={formState.streetAddress}
                zipCode={formState.zipCode}
              />
              {error?.messages?.[currentMessageIndex] &&
                !!error?.messages?.[currentMessageIndex].message?.length &&
                renderError(error?.messages?.[currentMessageIndex].message?.[0] || '')
              }
            </>
          ) :
          (
            <h5>We are sorry but your address does not match any of the officials. </h5>
          ))}
      {phoneChannel && actionCenterType === phoneType && (
        <>
          <FormHeading>Call talking points</FormHeading>
          <TalkingPointsList>
            {talkingPoints.split('\n').map(talkingPoint => (
              <TalkingPointItem
                backgroundcolor={backgroundColor}
                key={talkingPoint}
              >
                {talkingPoint}
              </TalkingPointItem>
            ))}
          </TalkingPointsList>
          <Greeting
            isEmailAction={false}
            targets={previewTargets}
            mode={mode}
          />
        </>
      )}
      <SubmitSection>
        <SubmitButtonWrapper>
          {actionCenterType === emailType &&
            subjectMessagePairs.length > 1 &&
            currentMessageIndex > 0 &&
            subjectMessagePairs.length <= currentMessageIndex + 1 &&
            (
              <StepButton
                backgroundcolor={buttonColor}
                disabled={isPending}
                textcolor={textColor}
                type="button"
                value="Previous"
                onClick={() => setCurrentMessageIndex((prevValue: number) => prevValue - 1)}
              />
            )}
          {actionCenterType === emailType &&
            subjectMessagePairs.length > 1 &&
            subjectMessagePairs.length > currentMessageIndex + 1 &&
            (
              <StepButton
                backgroundcolor={buttonColor}
                disabled={isPending}
                textcolor={textColor}
                type="button"
                value="Next"
                onClick={() => setCurrentMessageIndex((prevValue: number) => prevValue + 1)}
              />
            )}
          {
            (subjectMessagePairs.length === currentMessageIndex + 1 ||
              actionCenterType !== emailType) &&
            (
              <SendButton
                backgroundcolor={buttonColor}
                disabled={isPending}
                textcolor={textColor}
                type="submit"
                value={actButtonText()}
              />
            )}
        </SubmitButtonWrapper>
        {hasAnyError(error) && (
          <>
            <GeneralErrorMessage>
              Please see errors above
            </GeneralErrorMessage>
            {
              currentMessageIndex !== 0 &&
              (
                !!error.messages?.[0]?.message?.length ||
                !!error.messages?.[0]?.subject?.length
              ) && (
                <CenteredErrorMessage>
                  Check the first message - click previous button
                </CenteredErrorMessage>
              )
            }
          </>
        )}
      </SubmitSection>
      <OptCheckboxes
        isOptInEmail={isOptInEmail}
        isOptInText={isOptInText}
        backgroundColor={buttonColor}
        handleOptChange={handleOptChange}
        optIn={optIn}
      />
    </>
  );
};

TakeActionFormContainer.defaultProps = {
  handleBlurAddress: () => null,
  hideMessageHeadline: false,
  isDisabledInputText: false,
  setActionCenterType: () => null,
  showPickActionButton: false,
};
